import { useEffect, useState } from 'react';
import groupBy from 'lodash.groupby';
import { CustomerAgreement, CustomerAgreementDictionary } from '../types';
import {
  useLazyGetCustomerAgreementFileQuery,
  useLazyGetCustomerAgreementsQuery,
  useLazyGetCustomerUnsignedAgreementsQuery,
} from '../../../customers/store/services/customers';
import getAgreementTypeName from '../../../new-application/helpers/get-agreement-type-name';

const useAgreements = ({
  agreements,
  usignedAgreements,
  customerId,
}: {
  agreements?: CustomerAgreement[];
  usignedAgreements?: CustomerAgreement[];
  customerId?: string;
}) => {
  const [agreementsVisible, setAgreementsVisible] = useState(false);
  const [agreementsList, setAgreementsList] = useState<
    CustomerAgreementDictionary<CustomerAgreement>
  >({});
  const [downloadedFileId, setDownloadedFileId] = useState('');

  const [
    getAgreements,
    { isLoading: agreementsIsLoading, isSuccess: agreementsLoadingIsSuccess },
  ] = useLazyGetCustomerAgreementsQuery();
  const [
    getUnsignedAgreements,
    {
      isLoading: usignedAgreementsIsLoading,
      isSuccess: unsignedAgreementsLoadingIsSuccess,
    },
  ] = useLazyGetCustomerUnsignedAgreementsQuery();
  const [
    getAgreementFile,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetCustomerAgreementFileQuery();

  const getAgreementFileAction = async (agreement: CustomerAgreement) => {
    setDownloadedFileId(agreement.id);

    const response = await getAgreementFile(agreement.id);

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${getAgreementTypeName(
        agreement?.type,
      )}(v${agreement?.version})`;
      hiddenElement.click();

      setDownloadedFileId('');
    }
  };

  useEffect(() => {
    if (agreementsLoadingIsSuccess && unsignedAgreementsLoadingIsSuccess) {
      const nextAgreements: CustomerAgreement[] = [];

      if (agreements?.length) {
        agreements.forEach((item) => {
          nextAgreements.push(item);
        });
      }

      if (usignedAgreements?.length) {
        usignedAgreements.forEach((item) => {
          nextAgreements.push(item);
        });
      }

      const groupedAgreements = groupBy(
        nextAgreements.sort((a, b) => b.version - a.version),
        (match) => match.type,
      );

      setAgreementsList(groupedAgreements);
    }
  }, [
    agreements,
    usignedAgreements,
    agreementsLoadingIsSuccess,
    unsignedAgreementsLoadingIsSuccess,
  ]);

  useEffect(() => {
    if (customerId) {
      getAgreements(customerId);
      getUnsignedAgreements(customerId);
    }
  }, [customerId]);

  return {
    agreementsIsLoading,
    usignedAgreementsIsLoading,
    setAgreementsVisible,
    agreementsVisible,
    agreementsList,
    downloadInProgress,
    downloadIsFetching,
    downloadedFileId,
    getAgreementFileAction,
  };
};

export default useAgreements;
